// ClockEntryModal — captures the "actual" payroll layer (time_entries).
// The cardinal rule: a wall-clock time + IANA zone is converted to a UTC instant
// at capture (wallToInstant); worked hours are the diff of two instants. There is
// no wall-time subtraction here, and clock_*_at is NEVER set from now() — so crossing
// time zones can't pollute hours, and back-dated entry produces the same instant as
// same-day entry. The zone defaults to the WORKED day's location, never "now".
import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import { supabase } from './supabaseClient.js';
import { useAuth } from './AuthContext.js';
import { wallToInstant, workedHours, formatInOriginZone, isCrossingDay, BC_ZONE_PRESETS } from './timeEntryHours.js';
import { zoneForLocation } from './zoneResolve.js';

const DEFAULT_ZONE = 'America/Vancouver';

// Stored instant → wall time in its origin zone, for editing an existing entry.
const toWall = (utcISO, zone) =>
  utcISO && zone ? DateTime.fromISO(utcISO, { zone: 'utc' }).setZone(zone).toFormat('HH:mm') : '';

const safeInstant = (date, wall, zone) => {
  try { return wallToInstant(date, wall, zone); } catch { return null; }
};

const zoneLabel = (z) => BC_ZONE_PRESETS.find((p) => p.zone === z)?.label || z;

// City label from an address — last two comma parts, e.g.
// '1221 10th Ave N, Golden, BC' → 'Golden, BC'.
const cityOf = (addr) => {
  if (!addr) return '';
  const parts = addr.split(',').map((s) => s.trim()).filter(Boolean);
  if (parts.length <= 1) return parts[0] || '';
  return parts.slice(-2).join(', ');
};

const friendlyError = (e) => {
  const msg = e?.message || String(e);
  if (/time_entries/i.test(msg) || e?.code === '42P01') {
    return 'Time tracking isn’t set up yet — apply migration 013 in Supabase, then try again.';
  }
  return msg;
};

const ClockEntryModal = ({ route, dayNumber, mode, workDate, startLocation, endLocation, existingEntry, onClose, onSaved }) => {
  const { user } = useAuth();
  const existing = existingEntry || null;
  const showEnd = mode !== 'in';        // start-only when first clocking in
  const startEditable = mode !== 'out'; // in/edit can edit the start; out shows it read-only

  const [inWall, setInWall] = useState(existing?.clock_in_at ? toWall(existing.clock_in_at, existing.clock_in_zone) : '08:00');
  const [inZone, setInZone] = useState(existing?.clock_in_zone || '');
  const [inLoc, setInLoc] = useState(existing?.clock_in_location || startLocation || '');

  const [outWall, setOutWall] = useState(existing?.clock_out_at ? toWall(existing.clock_out_at, existing.clock_out_zone) : '17:00');
  const [outZone, setOutZone] = useState(existing?.clock_out_zone || '');
  const [outLoc, setOutLoc] = useState(existing?.clock_out_location || endLocation || '');
  const [confirmEnd, setConfirmEnd] = useState(existing?.location_confirmed || false);

  const [notes, setNotes] = useState(existing?.notes || '');
  const [detecting, setDetecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Offline zone prefill from the day's start/end physical location (tz-lookup, no API).
  // Only fills zones not already set, so we never clobber a saved/confirmed zone.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDetecting(true);
      try {
        if (!inZone && startLocation) {
          const z = await zoneForLocation(startLocation);
          if (!cancelled && z) setInZone(z);
        }
        if (showEnd && !outZone && endLocation) {
          const z = await zoneForLocation(endLocation);
          if (!cancelled && z) setOutZone(z);
        }
      } finally {
        if (!cancelled) setDetecting(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const effInZone = inZone || DEFAULT_ZONE;
  const effOutZone = outZone || DEFAULT_ZONE;

  // Live preview — worked time as an instant diff; crossing = start zone != end zone.
  const previewInAt = mode === 'out' ? existing?.clock_in_at : safeInstant(workDate, inWall, effInZone);
  const previewInZone = mode === 'out' ? existing?.clock_in_zone : effInZone;
  const previewOutAt = showEnd && outWall ? safeInstant(workDate, outWall, effOutZone) : null;
  const previewHours = previewInAt && previewOutAt ? workedHours(previewInAt, previewOutAt) : null;
  const crossed = isCrossingDay([previewInZone, previewOutAt ? effOutZone : null]);

  const handleSave = async () => {
    setError(null);

    if (mode === 'in') {
      const at = safeInstant(workDate, inWall, effInZone);
      if (!at) { setError('Enter a valid start time (HH:mm).'); return; }
      setSaving(true);
      const fields = { clock_in_at: at, clock_in_zone: effInZone, clock_in_location: inLoc.trim() || null };
      let e;
      if (existing) {
        // A row already exists for this day (e.g. general notes added first) — set its start time.
        ({ error: e } = await supabase.from('time_entries')
          .update({ ...fields, route_id: existing.route_id || route.id })
          .eq('id', existing.id));
      } else {
        ({ error: e } = await supabase.from('time_entries').insert([{
          employee_id: route.applicator_id,
          work_date: workDate,
          ...fields,
          route_id: route.id,
          created_by: user?.id || null,
        }]));
      }
      setSaving(false);
      if (e) { setError(friendlyError(e)); return; }
      onSaved();
      return;
    }

    // 'out' / 'edit' operate on the existing row.
    const inAt = mode === 'edit' ? safeInstant(workDate, inWall, effInZone) : existing.clock_in_at;
    const inZ = mode === 'edit' ? effInZone : existing.clock_in_zone;
    if (!inAt) { setError('Enter a valid start time (HH:mm).'); return; }

    let outAt = null;
    let outZ = null;
    if (outWall) {
      outZ = effOutZone;
      outAt = safeInstant(workDate, outWall, outZ);
      if (!outAt) { setError('Enter a valid stop time (HH:mm).'); return; }
      const wh = workedHours(inAt, outAt);
      if (wh == null || wh <= 0) { setError('Stop time must be after start time — worked hours must be positive.'); return; }
    }
    const crossedNow = isCrossingDay([inZ, outAt ? outZ : null]);
    if (outAt && crossedNow && !confirmEnd) {
      setError('This day crossed a time zone — confirm the stop location to save.');
      return;
    }

    const payload = {
      clock_out_at: outAt,
      clock_out_zone: outAt ? outZ : null,
      clock_out_location: outAt ? (outLoc.trim() || null) : null,
      crossed_timezones: !!outAt && crossedNow,
      location_confirmed: confirmEnd,
      notes: notes.trim() || null,
    };
    if (mode === 'edit') {
      payload.clock_in_at = inAt;
      payload.clock_in_zone = inZ;
      payload.clock_in_location = inLoc.trim() || null;
    }

    setSaving(true);
    const { error: e } = await supabase.from('time_entries').update(payload).eq('id', existing.id);
    setSaving(false);
    if (e) { setError(friendlyError(e)); return; }
    onSaved();
  };

  const inputClass =
    'w-full px-3 py-1.5 bg-gray-900 border border-gray-500 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600';
  const title = mode === 'in' ? 'Start Time' : mode === 'out' ? 'Stop Time' : 'Edit Times';

  const renderZoneSelect = (value, onChange) => {
    const v = value || DEFAULT_ZONE;
    const known = BC_ZONE_PRESETS.some((p) => p.zone === v);
    return (
      <select value={v} onChange={(e) => onChange(e.target.value)} className={inputClass}>
        {!known && <option value={v}>{v}</option>}
        {BC_ZONE_PRESETS.map((p) => (
          <option key={p.zone} value={p.zone}>{p.label}</option>
        ))}
      </select>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-12 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-900/80" onClick={onClose} />
      <div className="relative bg-gray-700 rounded-lg shadow-xl max-w-md w-full p-6 mb-12">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-white">{title} — Day {dayNumber}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">&times;</button>
        </div>
        <p className="text-xs text-gray-400 mb-4">
          {workDate}{detecting ? ' · detecting time zone…' : ''}
        </p>

        {error && (
          <div className="mb-4 px-3 py-2 bg-red-500/20 border border-red-500 rounded text-sm text-red-200">{error}</div>
        )}

        <div className="space-y-4">
          {/* Start of day */}
          <div className="space-y-2">
            <div className="text-xs font-semibold text-green-400 uppercase tracking-wide">Start of day</div>
            {startEditable ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Time</label>
                    <input type="time" value={inWall} onChange={(e) => setInWall(e.target.value)} className={inputClass} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Time zone</label>
                    {renderZoneSelect(inZone, setInZone)}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Location</label>
                  <input type="text" value={inLoc} onChange={(e) => setInLoc(e.target.value)} placeholder="Where you started" className={inputClass} />
                </div>
              </>
            ) : (
              <div className="text-sm text-gray-300">
                {formatInOriginZone(existing.clock_in_at, existing.clock_in_zone)}
                <span className="text-gray-400"> · {existing.clock_in_location || zoneLabel(existing.clock_in_zone)}</span>
              </div>
            )}
          </div>

          {/* End of day */}
          {showEnd && (
            <div className="space-y-2 pt-3 border-t border-gray-500/30">
              <div className="text-xs font-semibold text-blue-300 uppercase tracking-wide">End of day</div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Time</label>
                  <input type="time" value={outWall} onChange={(e) => setOutWall(e.target.value)} className={inputClass} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Time zone</label>
                  {renderZoneSelect(outZone, setOutZone)}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-300 mb-1">Location</label>
                <input type="text" value={outLoc} onChange={(e) => setOutLoc(e.target.value)} placeholder="Where you ended" className={inputClass} />
              </div>

              {crossed && (
                <label className="flex items-start gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/30 rounded cursor-pointer">
                  <input type="checkbox" checked={confirmEnd} onChange={(e) => setConfirmEnd(e.target.checked)} className="mt-0.5" />
                  <span className="text-xs text-blue-200 leading-tight">
                    This day crossed a time zone ({zoneLabel(previewInZone)} → {zoneLabel(effOutZone)}).
                    Confirm you ended in <span className="text-white">{cityOf(outLoc) || 'this city'}</span>.
                  </span>
                </label>
              )}
            </div>
          )}

          {/* Worked-hours preview */}
          {previewHours != null && (
            <div className="px-3 py-2 bg-gray-900/60 rounded text-sm flex items-center justify-between">
              <span className="text-gray-400">Worked time</span>
              <span className="font-semibold text-white">
                {previewHours.toFixed(2)} h
                {crossed && <span className="text-blue-300 font-normal"> · crossed TZ</span>}
              </span>
            </div>
          )}

          {/* Notes */}
          {showEnd && (
            <div>
              <label className="block text-xs font-medium text-gray-300 mb-1">Notes (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={inputClass} />
            </div>
          )}
        </div>

        <div className="flex items-center justify-end pt-4 mt-4 border-t border-gray-500/30 gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors">Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-green-700 text-white rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : mode === 'in' ? 'Set Start Time' : mode === 'out' ? 'Set Stop Time' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClockEntryModal;
